import { useMemo } from 'react';
import { useDebounce } from './useDebounce';

const DEFAULT_KEY = 'name';

/**
 * Custom hook to filter an array of objects based on a search string and specified keys.
 *
 * @param initData - Initial data array to filter.
 * @param search - Search string to filter the data.
 * @param keys - Array of keys to check against the search string. Defaults to 'name'.
 * @returns Filtered data array.
 */
export const useFilterEntity = <
  T extends { [key in K]: string },
  K extends string = typeof DEFAULT_KEY,
>(
  initData: T[] | null,
  search: string,
  keys: K[] = [DEFAULT_KEY] as K[] // Default to filtering by 'name'
): T[] => {
  const searchD = useDebounce(search);

  const data = useMemo(() => {
    if (!initData) return [];
    if (!searchD) return initData;
    return initData.filter((item) =>
      keys.some((key) => item[key]?.toLowerCase().includes(searchD.toLowerCase()))
    );
  }, [initData, searchD, keys]);

  return data;
};
